import { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Car, 
  MessageSquare, 
  Image, 
  LogOut,
  ExternalLink,
  Menu,
  X,
  Settings,
  Mail,
  KeyRound,
  Users,
  BarChart3,
  FileText,
  Newspaper,
  HelpCircle,
  ChevronRight,
  Star,
  Play,
  Calculator,
  Bell,
  Heart,
  Scale,
  Palette,
  Globe,
  Shield,
  CalendarCheck,
  Wrench,
  Key,
  Eye,
  Share2,
  Moon,
  Languages,
  Search,
  Pen,
  Send,
  Upload,
  Download,
  CheckCircle,
  Lock,
  PlusCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { AdminLanguageProvider, useAdminLanguage } from '@/context/AdminLanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin translations - comprehensive
const ADMIN_TRANSLATIONS = {
  de: {
    // Navigation
    dashboard: 'Dashboard',
    analytics: 'Analytics',
    vehicles: 'Fahrzeuge',
    test_drive: 'Probefahrten',
    customers: 'Kunden',
    contracts: 'Verträge',
    inquiries: 'Anfragen',
    car_requests: 'Fahrzeugsuche',
    blog: 'Blog',
    newsletter: 'Newsletter',
    media: 'Medien',
    settings: 'Einstellungen',
    password: 'Passwort ändern',
    support: 'Support',
    logout: 'Abmelden',
    help: 'Hilfe',
    setup_guide: 'Ersteinrichtung',
    features: 'Funktionen',
    setup: 'Einrichtung',
    // Common
    save: 'Speichern',
    saved: 'Gespeichert',
    saving: 'Speichern...',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    add: 'Hinzufügen',
    search: 'Suchen',
    filter: 'Filter',
    all: 'Alle',
    none: 'Keine',
    yes: 'Ja',
    no: 'Nein',
    // Status
    online: 'Online',
    offline: 'Offline',
    online_manual: 'Online (manuell)',
    offline_manual: 'Offline (manuell)',
    always_online: 'Immer Online',
    always_offline: 'Immer Offline',
    by_schedule: 'Nach Zeitplan',
    online_settings: 'Online-Status Einstellungen',
    manual_status: 'Manueller Status',
    schedule: 'Zeitplan',
    // Settings tabs
    general: 'Allgemein',
    modules: 'Module',
    navigation: 'Navigation',
    content: 'Inhalte',
    contact: 'Kontakt',
    financing: 'Finanzierung',
    design: 'Design',
    seo: 'SEO',
    legal: 'Rechtliches',
    api_keys: 'API Keys',
    categories: 'Kategorien',
    accounts: 'Accounts',
    // Dashboard
    welcome: 'Willkommen',
    total_vehicles: 'Fahrzeuge gesamt',
    active_vehicles: 'Aktive Fahrzeuge',
    sold_vehicles: 'Verkaufte Fahrzeuge',
    open_inquiries: 'Offene Anfragen',
    recent_activity: 'Letzte Aktivitäten',
    view_website: 'Website ansehen',
    logged_in_as: 'Angemeldet als',
    // Days
    monday: 'Montag',
    tuesday: 'Dienstag',
    wednesday: 'Mittwoch',
    thursday: 'Donnerstag',
    friday: 'Freitag',
    saturday: 'Samstag',
    sunday: 'Sonntag',
    closed: 'Geschlossen'
  },
  en: {
    // Navigation
    dashboard: 'Dashboard',
    analytics: 'Analytics',
    vehicles: 'Vehicles',
    test_drive: 'Test Drives',
    customers: 'Customers',
    contracts: 'Contracts',
    inquiries: 'Inquiries',
    car_requests: 'Car Requests',
    blog: 'Blog',
    newsletter: 'Newsletter',
    media: 'Media',
    settings: 'Settings',
    password: 'Change Password',
    support: 'Support',
    logout: 'Logout',
    help: 'Help',
    setup_guide: 'Setup Guide',
    features: 'Features',
    setup: 'Setup',
    // Common
    save: 'Save',
    saved: 'Saved',
    saving: 'Saving...',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    // Status
    online: 'Online',
    offline: 'Offline',
    online_manual: 'Online (manual)',
    offline_manual: 'Offline (manual)',
    always_online: 'Always Online',
    always_offline: 'Always Offline',
    by_schedule: 'By Schedule',
    online_settings: 'Online Status Settings',
    manual_status: 'Manual Status',
    schedule: 'Schedule',
    // Settings tabs
    general: 'General',
    modules: 'Modules',
    navigation: 'Navigation',
    content: 'Content',
    contact: 'Contact',
    financing: 'Financing',
    design: 'Design',
    seo: 'SEO',
    legal: 'Legal',
    api_keys: 'API Keys',
    categories: 'Categories',
    accounts: 'Accounts',
    // Dashboard
    welcome: 'Welcome',
    total_vehicles: 'Total Vehicles',
    active_vehicles: 'Active Vehicles',
    sold_vehicles: 'Sold Vehicles',
    open_inquiries: 'Open Inquiries',
    recent_activity: 'Recent Activity',
    view_website: 'View Website',
    logged_in_as: 'Logged in as',
    // Days
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    closed: 'Closed'
  }
};

function AdminLayout() {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const { settings } = useSiteSettings();
  const { adminLang, toggleAdminLang, setLanguage, t } = useAdminLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [guideTab, setGuideTab] = useState('features'); // 'features' or 'setup'
  const [guideLang, setGuideLang] = useState('de'); // 'de' or 'en'

  // Online status modal state - must be before any conditional returns
  const [showOnlineModal, setShowOnlineModal] = useState(false);
  const [onlineHours, setOnlineHours] = useState({
    monday: { enabled: true, start: '09:00', end: '18:00' },
    tuesday: { enabled: true, start: '09:00', end: '18:00' },
    wednesday: { enabled: true, start: '09:00', end: '18:00' },
    thursday: { enabled: true, start: '09:00', end: '18:00' },
    friday: { enabled: true, start: '09:00', end: '18:00' },
    saturday: { enabled: true, start: '09:00', end: '14:00' },
    sunday: { enabled: false, start: '00:00', end: '00:00' }
  });
  const [manualOnlineOverride, setManualOnlineOverride] = useState(null);

  // Local translation helper that falls back to ADMIN_TRANSLATIONS
  const tLocal = (key) => {
    // First try the context translation
    const contextTranslation = t(key);
    if (contextTranslation !== key) return contextTranslation;
    // Fall back to local translations
    return ADMIN_TRANSLATIONS[adminLang]?.[key] || ADMIN_TRANSLATIONS.de[key] || key;
  };

  // Update online hours when settings change
  useEffect(() => {
    if (settings?.online_status_hours) {
      setOnlineHours(settings.online_status_hours);
    }
    if (settings?.manual_online_override !== undefined) {
      setManualOnlineOverride(settings.manual_online_override);
    }
  }, [settings]);

  // Get logo URL for admin (light mode)
  const getLogoUrl = () => {
    if (settings?.logo_light_url) {
      return settings.logo_light_url.startsWith('http') 
        ? settings.logo_light_url 
        : `${API_URL}${settings.logo_light_url}`;
    }
    return null;
  };

  const logoUrl = getLogoUrl();

  // Check if we're on the dashboard (app grid view)
  const isDashboard = location.pathname === '/admin' || location.pathname === '/admin/';

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check if user is main admin
  const isMainAdmin = user?.is_main_admin || user?.username === 'admin';

  // Check if user has permission for a module
  const hasPermission = (permissionKey) => {
    if (isMainAdmin) return true;
    if (!permissionKey) return true;
    return user?.permissions?.[permissionKey] === true;
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, labelKey: 'dashboard', exact: true, permissionKey: null },
    { path: '/admin/analytics', icon: BarChart3, labelKey: 'analytics', permissionKey: 'analytics' },
    { path: '/admin/cars', icon: Car, labelKey: 'vehicles', permissionKey: 'cars' },
    { path: '/admin/test-drive', icon: CalendarCheck, labelKey: 'test_drive', permissionKey: 'test_drive' },
    { path: '/admin/customers', icon: Users, labelKey: 'customers', permissionKey: 'customers' },
    { path: '/admin/contracts', icon: FileText, labelKey: 'contracts', permissionKey: 'contracts' },
    { path: '/admin/inquiries', icon: MessageSquare, labelKey: 'inquiries', permissionKey: 'inquiries' },
    { path: '/admin/car-requests', icon: Search, labelKey: 'car_requests', permissionKey: null },
    { path: '/admin/blog', icon: Newspaper, labelKey: 'blog', permissionKey: 'blog' },
    { path: '/admin/newsletter', icon: Mail, labelKey: 'newsletter', permissionKey: 'newsletter' },
    { path: '/admin/settings', icon: Settings, labelKey: 'settings', permissionKey: 'settings' },
    { path: '/admin/support', icon: HelpCircle, labelKey: 'support', permissionKey: null },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Check if currently online based on online status hours (separate from test drive hours)
  const isCurrentlyOnline = () => {
    // Manual override takes precedence
    if (manualOnlineOverride !== null) return manualOnlineOverride;
    
    const now = new Date();
    const dayIndex = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayKeys[dayIndex];
    
    const hours = onlineHours || {};
    const dayHours = hours[currentDay];
    
    if (!dayHours || !dayHours.enabled) return false;
    
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [startH, startM] = (dayHours.start || '09:00').split(':').map(Number);
    const [endH, endM] = (dayHours.end || '18:00').split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    
    return currentTime >= startMinutes && currentTime <= endMinutes;
  };

  const saveOnlineSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          online_status_hours: onlineHours,
          manual_online_override: manualOnlineOverride
        })
      });
      setShowOnlineModal(false);
    } catch (error) {
      console.error('Error saving online settings:', error);
    }
  };

  const online = isCurrentlyOnline();

  return (
    <div className="min-h-screen bg-[#F9F9F7] admin-layout" data-theme="light">
      {/* Dashboard Mode - No Sidebar, Simple Header */}
      {isDashboard ? (
        <>
          {/* Simple Header for Dashboard */}
          <header className="bg-white border-b border-[#E5E5E5] px-4 md:px-8 py-4">
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <Link to="/" className="flex items-center">
                {logoUrl ? (
                  <img src={logoUrl} alt="NYZA-STUDIO" className="h-10 object-contain" />
                ) : (
                  <span className="font-heading text-xl font-bold tracking-tight">
                    <span style={{ fontFamily: "'Poppins', sans-serif" }}>NYZA-STUDIO</span> <span className="text-gold">CARDEALER</span>
                  </span>
                )}
              </Link>
              <div className="flex items-center gap-4">
                {/* Online Status - Clickable */}
                <button
                  onClick={() => setShowOnlineModal(true)}
                  className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F9F9F7] transition-colors"
                  title={adminLang === 'de' ? 'Online-Status einstellen' : 'Set online status'}
                >
                  <div className={`w-2.5 h-2.5 rounded-full ${online ? 'bg-green-500' : 'bg-gray-400'}`} />
                  <span className="text-xs text-[#666666] hidden sm:inline">
                    {manualOnlineOverride !== null 
                      ? (manualOnlineOverride ? (adminLang === 'de' ? 'Online (manuell)' : 'Online (manual)') : (adminLang === 'de' ? 'Offline (manuell)' : 'Offline (manual)'))
                      : (online ? 'Online' : 'Offline')
                    }
                  </span>
                </button>
                
                {/* Admin Language Toggle */}
                <div className="flex border border-[#E5E5E5] rounded-lg overflow-hidden">
                  <button
                    onClick={() => setLanguage('de')}
                    className={`px-2.5 py-1 text-sm font-medium transition-colors ${adminLang === 'de' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
                    data-testid="admin-lang-de"
                  >
                    DE
                  </button>
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-2.5 py-1 text-sm font-medium transition-colors ${adminLang === 'en' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
                    data-testid="admin-lang-en"
                  >
                    EN
                  </button>
                </div>
                {isMainAdmin && (
                  <button
                    onClick={() => setShowSetupGuide(true)}
                    className="relative p-2 rounded-full hover:bg-red-50 transition-colors"
                    title={adminLang === 'de' ? 'Hilfe' : 'Help'}
                    data-testid="setup-guide-btn"
                  >
                    <HelpCircle size={22} className="text-red-500" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-sm text-[#666666] hover:text-[#0A0A0A] transition-colors"
                >
                  <LogOut size={18} />
                  <span className="hidden sm:inline">{adminLang === 'de' ? 'Abmelden' : 'Logout'}</span>
                </button>
              </div>
            </div>
          </header>
          
          {/* Dashboard Content */}
          <main className="p-4 md:p-8">
            <Outlet />
          </main>
        </>
      ) : (
        <>
          {/* Regular Mode - With Sidebar */}
          {/* Mobile Header */}
          <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E5E5E5] h-14 flex items-center justify-between px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2"
              data-testid="mobile-menu-toggle"
            >
              <Menu size={24} />
            </button>
            <Link to="/admin" className="flex items-center">
              {logoUrl ? (
                <img src={logoUrl} alt="NYZA-STUDIO" className="h-8 object-contain" />
              ) : (
                <span className="font-heading text-lg font-bold tracking-tight">
                  <span style={{ fontFamily: "'Poppins', sans-serif" }}>NYZA</span><span className="text-gold">-STUDIO</span>
                </span>
              )}
            </Link>
            {isMainAdmin && (
              <button
                onClick={() => setShowSetupGuide(true)}
                className="relative p-2"
                title={adminLang === 'de' ? 'Ersteinrichtung' : 'Setup Guide'}
              >
                <HelpCircle size={20} className="text-red-500" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              </button>
            )}
          </header>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div 
              className="lg:hidden fixed inset-0 bg-black/50 z-50"
              onClick={() => setSidebarOpen(false)}
            />
          )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E5E5E5] z-50
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <Link to="/" className="flex items-center">
              {logoUrl ? (
                <img src={logoUrl} alt="NYZA-STUDIO" className="h-10 object-contain" />
              ) : (
                <span className="font-heading text-xl font-bold tracking-tight">
                  <span style={{ fontFamily: "'Poppins', sans-serif" }}>NYZA-STUDIO</span> <span className="text-gold">CARDEALER</span>
                </span>
              )}
            </Link>
            <p className="overline mt-1">Admin Panel</p>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const allowed = hasPermission(item.permissionKey);
              
              // If not allowed, show disabled item
              if (!allowed) {
                return (
                  <li key={item.path}>
                    <div
                      className="flex items-center gap-3 px-6 py-3 text-sm text-gray-400 cursor-not-allowed"
                      title={adminLang === 'de' ? 'Kein Zugriff' : 'No access'}
                    >
                      <item.icon size={18} strokeWidth={1.5} />
                      {t(item.labelKey)}
                      <Lock size={12} className="ml-auto" />
                    </div>
                  </li>
                );
              }
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                      isActive(item.path, item.exact)
                        ? 'sidebar-active'
                        : 'text-[#666666] hover:text-[#0A0A0A] hover:bg-[#F9F9F7]'
                    }`}
                    data-testid={`nav-${item.labelKey}`}
                  >
                    <item.icon size={18} strokeWidth={1.5} />
                    {t(item.labelKey)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E5E5E5] bg-white">
          {/* Online Status + Language Toggle Row */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#E5E5E5]">
            {/* Online Status - Clickable */}
            <button
              onClick={() => setShowOnlineModal(true)}
              className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F9F9F7] transition-colors"
              title={adminLang === 'de' ? 'Online-Status einstellen' : 'Set online status'}
            >
              <div className={`w-2.5 h-2.5 rounded-full ${online ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-xs text-[#666666]">
                {manualOnlineOverride !== null 
                  ? (manualOnlineOverride ? 'Online ✋' : 'Offline ✋')
                  : (online ? 'Online' : 'Offline')
                }
              </span>
            </button>
            
            {/* Admin Language Toggle */}
            <div className="flex border border-[#E5E5E5] rounded overflow-hidden">
              <button
                onClick={() => setLanguage('de')}
                className={`px-2 py-1 text-xs font-medium transition-colors ${adminLang === 'de' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
              >
                DE
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-2 py-1 text-xs font-medium transition-colors ${adminLang === 'en' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
              >
                EN
              </button>
            </div>
          </div>
          
          <Link
            to="/"
            target="_blank"
            className="flex items-center gap-2 text-sm text-[#666666] hover:text-gold transition-colors mb-3"
          >
            <ExternalLink size={16} />
            {t('sidebar_view_website')}
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-[#666666] hover:text-red-600 transition-colors w-full"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            {t('sidebar_logout')}
          </button>
          <p className="text-xs text-[#999999] mt-4">
            {t('sidebar_logged_in_as')} {user?.username}
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        {/* Desktop Header with Language Toggle and Setup Guide */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-8 py-4 border-b border-[#E5E5E5] bg-white">
          {/* Admin Language Toggle */}
          <div className="flex border border-[#E5E5E5] rounded-lg overflow-hidden">
            <button
              onClick={() => setLanguage('de')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${adminLang === 'de' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
              data-testid="admin-lang-de"
            >
              DE
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${adminLang === 'en' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
              data-testid="admin-lang-en"
            >
              EN
            </button>
          </div>
          {/* Setup Guide Button - Only for Main Admin */}
          {isMainAdmin && (
            <button
              onClick={() => setShowSetupGuide(true)}
              className="relative p-2 rounded-full hover:bg-red-50 transition-colors group"
              title={adminLang === 'de' ? 'Ersteinrichtung' : 'Setup Guide'}
              data-testid="setup-guide-btn"
            >
              <HelpCircle size={22} className="text-red-500" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            </button>
          )}
        </div>

        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
        </>
      )}

      {/* Setup Guide Modal */}
      {showSetupGuide && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-[#E5E5E5] p-4 md:p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                    <HelpCircle size={20} className="text-gold" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl">
                      {guideLang === 'de' ? 'System-Übersicht' : 'System Overview'}
                    </h2>
                    <p className="text-sm text-[#666666]">
                      {guideLang === 'de' ? 'Alle Funktionen & Ersteinrichtung' : 'All Features & Setup Guide'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Language Toggle */}
                  <div className="flex border border-[#E5E5E5] rounded-lg overflow-hidden">
                    <button
                      onClick={() => setGuideLang('de')}
                      className={`px-2 py-1 text-xs font-medium transition-colors ${guideLang === 'de' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
                    >
                      DE
                    </button>
                    <button
                      onClick={() => setGuideLang('en')}
                      className={`px-2 py-1 text-xs font-medium transition-colors ${guideLang === 'en' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F9F9F7]'}`}
                    >
                      EN
                    </button>
                  </div>
                  <button 
                    onClick={() => setShowSetupGuide(false)}
                    className="p-2 hover:bg-[#F9F9F7] rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              {/* Tab Navigation */}
              <div className="flex gap-2">
                <button
                  onClick={() => setGuideTab('features')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    guideTab === 'features' 
                      ? 'bg-gold text-white' 
                      : 'bg-[#F9F9F7] hover:bg-[#E5E5E5]'
                  }`}
                >
                  {guideLang === 'de' ? '🚀 Funktionen' : '🚀 Features'}
                </button>
                <button
                  onClick={() => setGuideTab('setup')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    guideTab === 'setup' 
                      ? 'bg-gold text-white' 
                      : 'bg-[#F9F9F7] hover:bg-[#E5E5E5]'
                  }`}
                >
                  {guideLang === 'de' ? '⚙️ Ersteinrichtung' : '⚙️ Setup Guide'}
                </button>
              </div>
            </div>

            <div className="p-4 md:p-6">
              {/* FEATURES TAB */}
              {guideTab === 'features' && (
                <div className="space-y-6">
                  {/* Fahrzeugverwaltung */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <Car className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Fahrzeugverwaltung' : 'Vehicle Management'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={Car}
                        title={guideLang === 'de' ? 'Fahrzeuge verwalten' : 'Manage Vehicles'}
                        description={guideLang === 'de' 
                          ? 'Alle Fahrzeuge anzeigen, bearbeiten, löschen. Suche & Filter nach Status.'
                          : 'View, edit, delete all vehicles. Search & filter by status.'}
                        location={guideLang === 'de' ? 'Fahrzeuge' : 'Vehicles'}
                        link="/admin/cars"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/cars'); }}
                      />
                      <FeatureBox
                        icon={PlusCircle}
                        title={guideLang === 'de' ? 'Neues Fahrzeug anlegen' : 'Add New Vehicle'}
                        description={guideLang === 'de' 
                          ? 'Marke, Modell, Preis, PS, Fotos hochladen/URLs, Beschreibung DE/EN, Standort, Kilometerstand, EZ, Vorbesitzer.'
                          : 'Brand, model, price, HP, upload photos/URLs, description DE/EN, location, mileage, registration date, previous owners.'}
                        location={guideLang === 'de' ? 'Fahrzeuge → + Neues Fahrzeug' : 'Vehicles → + New Vehicle'}
                        link="/admin/cars/new"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/cars/new'); }}
                      />
                      <FeatureBox
                        icon={Settings}
                        title={guideLang === 'de' ? 'Kategorien verwalten' : 'Manage Categories'}
                        description={guideLang === 'de' 
                          ? 'Kraftstoff-Typen, Getriebe, Antriebsarten und Farben definieren. Neue Optionen hinzufügen.'
                          : 'Define fuel types, transmissions, drivetrains and colors. Add new options.'}
                        location={guideLang === 'de' ? 'Fahrzeuge → Tab "Kategorien"' : 'Vehicles → Tab "Categories"'}
                        link="/admin/cars"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/cars'); }}
                      />
                      <FeatureBox
                        icon={Star}
                        title={guideLang === 'de' ? 'Empfohlene Fahrzeuge' : 'Featured Vehicles'}
                        description={guideLang === 'de' 
                          ? 'Fahrzeuge auf der Startseite hervorheben. "Hervorgehoben" Checkbox im Fahrzeug-Formular.'
                          : 'Highlight vehicles on homepage. "Featured" checkbox in vehicle form.'}
                        location={guideLang === 'de' ? 'Fahrzeug bearbeiten → Hervorgehoben' : 'Edit vehicle → Featured'}
                        link="/admin/cars"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/cars'); }}
                      />
                      <FeatureBox
                        icon={CheckCircle}
                        title={guideLang === 'de' ? 'Als verkauft markieren' : 'Mark as Sold'}
                        description={guideLang === 'de' 
                          ? 'Verkaufte Fahrzeuge mit Banner markieren. Anpassbares Banner-Design.'
                          : 'Mark sold vehicles with banner. Customizable banner design.'}
                        location={guideLang === 'de' ? 'Fahrzeug bearbeiten → Verkauft' : 'Edit vehicle → Sold'}
                        link="/admin/cars"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/cars'); }}
                      />
                    </div>
                  </div>

                  {/* Probefahrten */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <CalendarCheck className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Probefahrten' : 'Test Drives'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={CalendarCheck}
                        title={guideLang === 'de' ? 'Probefahrt-Buchungen' : 'Test Drive Bookings'}
                        description={guideLang === 'de' 
                          ? 'Kunden können Probefahrten online buchen. Kalenderansicht aller Termine.'
                          : 'Customers can book test drives online. Calendar view of all appointments.'}
                        location={guideLang === 'de' ? 'Probefahrten' : 'Test Drives'}
                        link="/admin/test-drive"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/test-drive'); }}
                      />
                      <FeatureBox
                        icon={Settings}
                        title={guideLang === 'de' ? 'Probefahrt-Einstellungen' : 'Test Drive Settings'}
                        description={guideLang === 'de' 
                          ? 'Verfügbare Zeiten, Dauer, Bestätigungs-E-Mails konfigurieren. Probefahrt aktivieren/deaktivieren.'
                          : 'Configure available times, duration, confirmation emails. Enable/disable test drives.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → Probefahrt ⚙️' : 'Settings → Modules → Test Drive ⚙️'}
                        link="/admin/module-settings/test-drive"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/test-drive'); }}
                      />
                      <FeatureBox
                        icon={CheckCircle}
                        title={guideLang === 'de' ? 'Termine bestätigen/absagen' : 'Confirm/Cancel Appointments'}
                        description={guideLang === 'de' 
                          ? 'Probefahrten annehmen oder absagen. Status wird automatisch aktualisiert.'
                          : 'Accept or cancel test drives. Status updates automatically.'}
                        location={guideLang === 'de' ? 'Probefahrten → Termin anklicken' : 'Test Drives → Click appointment'}
                        link="/admin/test-drive"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/test-drive'); }}
                      />
                    </div>
                  </div>

                  {/* Kundenbereich */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <Users className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Kundenmanagement (CRM)' : 'Customer Management (CRM)'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={Users}
                        title={guideLang === 'de' ? 'Kundendatenbank' : 'Customer Database'}
                        description={guideLang === 'de' 
                          ? 'Alle Kunden verwalten mit Kontaktdaten, Status (Aktiv, VIP, Interessent), Notizen, Dateien.'
                          : 'Manage all customers with contact info, status (Active, VIP, Prospect), notes, files.'}
                        location={guideLang === 'de' ? 'Kunden' : 'Customers'}
                        link="/admin/customers"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/customers'); }}
                      />
                      <FeatureBox
                        icon={Heart}
                        title={guideLang === 'de' ? 'Fahrzeug-Interesse zuweisen' : 'Assign Vehicle Interest'}
                        description={guideLang === 'de' 
                          ? 'Kunden mit interessierten oder gekauften Fahrzeugen verknüpfen.'
                          : 'Link customers with interested or purchased vehicles.'}
                        location={guideLang === 'de' ? 'Kunden → Bearbeiten' : 'Customers → Edit'}
                        link="/admin/customers"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/customers'); }}
                      />
                      <FeatureBox
                        icon={Upload}
                        title={guideLang === 'de' ? 'Kundendateien hochladen' : 'Upload Customer Files'}
                        description={guideLang === 'de' 
                          ? 'Verträge, Dokumente und andere Dateien pro Kunde speichern.'
                          : 'Store contracts, documents and other files per customer.'}
                        location={guideLang === 'de' ? 'Kunden → Detail → Dateien' : 'Customers → Detail → Files'}
                        link="/admin/customers"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/customers'); }}
                      />
                    </div>
                  </div>

                  {/* Verträge */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <FileText className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Vertragssystem' : 'Contract System'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={FileText}
                        title={guideLang === 'de' ? 'Kaufverträge erstellen' : 'Create Purchase Contracts'}
                        description={guideLang === 'de' 
                          ? 'Kaufvertrag mit Kunde, Fahrzeug, Preis, Anzahlung, Garantie, Lieferdatum erstellen.'
                          : 'Create purchase contract with customer, vehicle, price, deposit, warranty, delivery date.'}
                        location={guideLang === 'de' ? 'Verträge → + Neuer Vertrag' : 'Contracts → + New Contract'}
                        link="/admin/contracts"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/contracts'); }}
                      />
                      <FeatureBox
                        icon={Pen}
                        title={guideLang === 'de' ? 'Händler-Unterschrift einrichten' : 'Setup Dealer Signature'}
                        description={guideLang === 'de' 
                          ? 'PNG-Bild Ihrer Unterschrift hochladen. Erscheint automatisch als Verkäufer-Unterschrift im PDF-Vertrag.'
                          : 'Upload PNG image of your signature. Automatically appears as seller signature in PDF contract.'}
                        location={guideLang === 'de' ? 'Verträge → Tab "Einstellungen"' : 'Contracts → Tab "Settings"'}
                        link="/admin/contracts"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/contracts'); }}
                      />
                      <FeatureBox
                        icon={Send}
                        title={guideLang === 'de' ? 'Vertrag per E-Mail senden' : 'Send Contract via Email'}
                        description={guideLang === 'de' 
                          ? 'Vertrag an Kunden-E-Mail senden mit Link zur digitalen Unterschrift.'
                          : 'Send contract to customer email with link for digital signature.'}
                        location={guideLang === 'de' ? 'Verträge → "Senden" Button' : 'Contracts → "Send" Button'}
                        link="/admin/contracts"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/contracts'); }}
                      />
                      <FeatureBox
                        icon={Pen}
                        title={guideLang === 'de' ? 'Digitale Unterschrift' : 'Digital Signature'}
                        description={guideLang === 'de' 
                          ? 'Kunde kann Vertrag online ansehen und digital unterschreiben. PDF mit beiden Unterschriften generieren.'
                          : 'Customer can view and digitally sign contract online. Generate PDF with both signatures.'}
                        location={guideLang === 'de' ? 'Öffentlicher Link /contract/{id}' : 'Public link /contract/{id}'}
                        link="/admin/contracts"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/contracts'); }}
                      />
                      <FeatureBox
                        icon={Download}
                        title={guideLang === 'de' ? 'PDF mit Unterschriften' : 'PDF with Signatures'}
                        description={guideLang === 'de' 
                          ? 'Nach der Kunden-Unterschrift: PDF enthält beide Unterschriften (Händler + Kunde).'
                          : 'After customer signs: PDF includes both signatures (dealer + customer).'}
                        location={guideLang === 'de' ? 'Verträge → PDF herunterladen' : 'Contracts → Download PDF'}
                        link="/admin/contracts"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/contracts'); }}
                      />
                    </div>
                  </div>

                  {/* Anfragen & Kommunikation */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <MessageSquare className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Anfragen & Kommunikation' : 'Inquiries & Communication'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={MessageSquare}
                        title={guideLang === 'de' ? 'Anfragen verwalten' : 'Manage Inquiries'}
                        description={guideLang === 'de' 
                          ? 'Chat-ähnliche Konversation mit Kunden. Auto-Antwort im Tab "Auto-Antwort" konfigurierbar.'
                          : 'Chat-like conversation with customers. Auto-reply configurable in "Auto-Reply" tab.'}
                        location={guideLang === 'de' ? 'Anfragen' : 'Inquiries'}
                        link="/admin/inquiries"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/inquiries'); }}
                      />
                      <FeatureBox
                        icon={Mail}
                        title={guideLang === 'de' ? 'Newsletter System' : 'Newsletter System'}
                        description={guideLang === 'de' 
                          ? 'Abonnenten verwalten, Newsletter mit Fahrzeugen versenden.'
                          : 'Manage subscribers, send newsletters with vehicles.'}
                        location={guideLang === 'de' ? 'Newsletter' : 'Newsletter'}
                        link="/admin/newsletter"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/newsletter'); }}
                      />
                      <FeatureBox
                        icon={Bell}
                        title={guideLang === 'de' ? 'Preisalarme' : 'Price Alerts'}
                        description={guideLang === 'de' 
                          ? 'Kunden erhalten E-Mail bei Preissenkungen ihrer Wunschfahrzeuge.'
                          : 'Customers receive email when prices drop on their favorite vehicles.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module' : 'Settings → Modules'}
                        link="/admin/settings?tab=modules"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=modules'); }}
                      />
                    </div>
                  </div>

                  {/* Homepage Module */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <LayoutDashboard className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Homepage-Module' : 'Homepage Modules'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={Star}
                        title={guideLang === 'de' ? 'Empfohlene Fahrzeuge' : 'Featured Vehicles'}
                        description={guideLang === 'de' 
                          ? 'Hervorgehobene Fahrzeuge auf der Startseite anzeigen.'
                          : 'Display featured vehicles on homepage.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/module-settings/featured"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/featured'); }}
                      />
                      <FeatureBox
                        icon={Eye}
                        title={guideLang === 'de' ? 'Marken & Partner' : 'Brands & Partners'}
                        description={guideLang === 'de' 
                          ? 'Logo-Karussell mit Automarken und Partnern.'
                          : 'Logo carousel with car brands and partners.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/module-settings/brands"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/brands'); }}
                      />
                      <FeatureBox
                        icon={FileText}
                        title={guideLang === 'de' ? 'Über uns Bereich' : 'About Us Section'}
                        description={guideLang === 'de' 
                          ? 'Unternehmensbeschreibung mit Bild und Statistiken.'
                          : 'Company description with image and statistics.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/module-settings/about"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/about'); }}
                      />
                      <FeatureBox
                        icon={Play}
                        title={guideLang === 'de' ? 'Video-Bereich' : 'Video Section'}
                        description={guideLang === 'de' 
                          ? 'YouTube Video einbetten. Spielt inline ab mit Thumbnail.'
                          : 'Embed YouTube video. Plays inline with thumbnail.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/module-settings/video"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/video'); }}
                      />
                      <FeatureBox
                        icon={Wrench}
                        title={guideLang === 'de' ? 'Werkstatt & Services' : 'Workshop & Services'}
                        description={guideLang === 'de' 
                          ? 'Dienstleistungen wie HU/AU, Reifenservice, Finanzierung anzeigen.'
                          : 'Display services like inspection, tire service, financing.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/module-settings/services"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/services'); }}
                      />
                      <FeatureBox
                        icon={HelpCircle}
                        title={guideLang === 'de' ? 'FAQ-Bereich' : 'FAQ Section'}
                        description={guideLang === 'de' 
                          ? 'Häufige Fragen mit aufklappbaren Antworten (DE/EN).'
                          : 'Frequently asked questions with expandable answers (DE/EN).'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/module-settings/faq"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/faq'); }}
                      />
                      <FeatureBox
                        icon={Mail}
                        title={guideLang === 'de' ? 'Newsletter-Anmeldung' : 'Newsletter Signup'}
                        description={guideLang === 'de' 
                          ? 'Newsletter-Anmeldeformular auf der Startseite.'
                          : 'Newsletter signup form on homepage.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/module-settings/newsletter"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/newsletter'); }}
                      />
                      <FeatureBox
                        icon={MessageSquare}
                        title={guideLang === 'de' ? 'Kontakt-Bereich' : 'Contact Section'}
                        description={guideLang === 'de' 
                          ? 'Kontaktformular mit Adresse, Telefon, Öffnungszeiten.'
                          : 'Contact form with address, phone, opening hours.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/module-settings/contact"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/module-settings/contact'); }}
                      />
                    </div>
                  </div>

                  {/* Benutzer-Features */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <Heart className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Benutzer-Features' : 'User Features'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={Heart}
                        title={guideLang === 'de' ? 'Merkliste / Favoriten' : 'Favorites / Wishlist'}
                        description={guideLang === 'de' 
                          ? 'Besucher können Fahrzeuge zur Merkliste hinzufügen (localStorage).'
                          : 'Visitors can add vehicles to favorites (localStorage).'}
                        location={guideLang === 'de' ? 'Einstellungen → Module' : 'Settings → Modules'}
                        link="/admin/settings?tab=modules"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=modules'); }}
                      />
                      <FeatureBox
                        icon={Scale}
                        title={guideLang === 'de' ? 'Fahrzeugvergleich' : 'Vehicle Comparison'}
                        description={guideLang === 'de' 
                          ? 'Bis zu 3 Fahrzeuge nebeneinander vergleichen.'
                          : 'Compare up to 3 vehicles side by side.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module' : 'Settings → Modules'}
                        link="/admin/settings?tab=modules"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=modules'); }}
                      />
                      <FeatureBox
                        icon={Calculator}
                        title={guideLang === 'de' ? 'Finanzierungsrechner' : 'Financing Calculator'}
                        description={guideLang === 'de' 
                          ? 'Monatliche Rate berechnen mit Anzahlung, Laufzeit, Zinssatz.'
                          : 'Calculate monthly payment with down payment, term, interest rate.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → ⚙️' : 'Settings → Modules → ⚙️'}
                        link="/admin/settings?tab=financing"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=financing'); }}
                      />
                      <FeatureBox
                        icon={Share2}
                        title={guideLang === 'de' ? 'Teilen-Funktion' : 'Share Function'}
                        description={guideLang === 'de' 
                          ? 'Fahrzeuge per Social Media oder Link teilen.'
                          : 'Share vehicles via social media or link.'}
                        location={guideLang === 'de' ? 'Auf jeder Fahrzeugkarte' : 'On every vehicle card'}
                        link="/cars"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/cars'); }}
                      />
                      <FeatureBox
                        icon={Search}
                        title={guideLang === 'de' ? 'Erweiterte Suche & Filter' : 'Advanced Search & Filters'}
                        description={guideLang === 'de' 
                          ? 'Filtern nach Marke, Preis, Jahr, PS, Kraftstoff, Getriebe, Farbe, Kilometerstand.'
                          : 'Filter by brand, price, year, HP, fuel, transmission, color, mileage.'}
                        location={guideLang === 'de' ? 'Fahrzeuge-Seite' : 'Vehicles Page'}
                        link="/cars"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/cars'); }}
                      />
                    </div>
                  </div>

                  {/* Design & Anpassung */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <Palette className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Design & Anpassung' : 'Design & Customization'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={Moon}
                        title={guideLang === 'de' ? 'Dark/Light Mode' : 'Dark/Light Mode'}
                        description={guideLang === 'de' 
                          ? 'Dunkles oder helles Design. Benutzer können umschalten.'
                          : 'Dark or light design. Users can toggle.'}
                        location={guideLang === 'de' ? 'Einstellungen → Design' : 'Settings → Design'}
                        link="/admin/settings?tab=design"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=design'); }}
                      />
                      <FeatureBox
                        icon={Palette}
                        title={guideLang === 'de' ? 'Theme-Vorlagen' : 'Theme Presets'}
                        description={guideLang === 'de' 
                          ? 'Klassisch, Modern Dunkel, Neon Sport und mehr.'
                          : 'Classic, Modern Dark, Neon Sport and more.'}
                        location={guideLang === 'de' ? 'Einstellungen → Design → Themes' : 'Settings → Design → Themes'}
                        link="/admin/settings?tab=design"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=design'); }}
                      />
                      <FeatureBox
                        icon={Settings}
                        title={guideLang === 'de' ? 'Schriftarten wählen' : 'Select Fonts'}
                        description={guideLang === 'de' 
                          ? '10 verschiedene Font-Kombinationen verfügbar.'
                          : '10 different font combinations available.'}
                        location={guideLang === 'de' ? 'Einstellungen → Design → Schriftart' : 'Settings → Design → Font'}
                        link="/admin/settings?tab=design"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=design'); }}
                      />
                      <FeatureBox
                        icon={Image}
                        title={guideLang === 'de' ? 'Hero-Bereich anpassen' : 'Customize Hero Section'}
                        description={guideLang === 'de' 
                          ? 'Hintergrundbild, Titel, Untertitel, Buttons anpassen.'
                          : 'Customize background image, title, subtitle, buttons.'}
                        location={guideLang === 'de' ? 'Einstellungen → Inhalte' : 'Settings → Content'}
                        link="/admin/settings?tab=content"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=content'); }}
                      />
                      <FeatureBox
                        icon={Languages}
                        title={guideLang === 'de' ? 'Mehrsprachigkeit (DE/EN)' : 'Multi-Language (DE/EN)'}
                        description={guideLang === 'de' 
                          ? 'Alle Texte in Deutsch und Englisch konfigurierbar.'
                          : 'All texts configurable in German and English.'}
                        location={guideLang === 'de' ? 'Überall in Einstellungen' : 'Throughout Settings'}
                        link="/admin/settings"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings'); }}
                      />
                    </div>
                  </div>

                  {/* Blog & Content */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <Newspaper className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Blog & Content' : 'Blog & Content'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={Newspaper}
                        title={guideLang === 'de' ? 'Blog-Beiträge' : 'Blog Posts'}
                        description={guideLang === 'de' 
                          ? 'Artikel erstellen mit Titel, Inhalt, Bild (DE/EN).'
                          : 'Create articles with title, content, image (DE/EN).'}
                        location={guideLang === 'de' ? 'Admin → Blog' : 'Admin → Blog'}
                        link="/admin/blog"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/blog'); }}
                      />
                      <FeatureBox
                        icon={Users}
                        title={guideLang === 'de' ? 'Team-Seite' : 'Team Page'}
                        description={guideLang === 'de' 
                          ? 'Teammitglieder mit Foto, Name, Position anzeigen.'
                          : 'Display team members with photo, name, position.'}
                        location={guideLang === 'de' ? 'Einstellungen → Module → Team' : 'Settings → Modules → Team'}
                        link="/admin/settings?tab=modules"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=modules'); }}
                      />
                    </div>
                  </div>

                  {/* Analytics & Rechtliches */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <BarChart3 className="text-gold" size={20} />
                      {guideLang === 'de' ? 'Analytics & Rechtliches' : 'Analytics & Legal'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={BarChart3}
                        title={guideLang === 'de' ? 'Analytics Dashboard' : 'Analytics Dashboard'}
                        description={guideLang === 'de' 
                          ? 'Seitenaufrufe, Top-Fahrzeuge, Länder/Städte, Conversion-Rate.'
                          : 'Page views, top vehicles, countries/cities, conversion rate.'}
                        location={guideLang === 'de' ? 'Admin → Analytics' : 'Admin → Analytics'}
                        link="/admin/analytics"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/analytics'); }}
                      />
                      <FeatureBox
                        icon={Globe}
                        title={guideLang === 'de' ? 'SEO-Einstellungen' : 'SEO Settings'}
                        description={guideLang === 'de' 
                          ? 'Meta-Titel, Beschreibung, Keywords, Sitemap, robots.txt.'
                          : 'Meta title, description, keywords, sitemap, robots.txt.'}
                        location={guideLang === 'de' ? 'Einstellungen → SEO' : 'Settings → SEO'}
                        link="/admin/settings?tab=seo"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=seo'); }}
                      />
                      <FeatureBox
                        icon={Shield}
                        title={guideLang === 'de' ? 'Rechtliche Seiten' : 'Legal Pages'}
                        description={guideLang === 'de' 
                          ? 'Impressum, Datenschutz, AGB bearbeiten (HTML).'
                          : 'Edit imprint, privacy policy, terms (HTML).'}
                        location={guideLang === 'de' ? 'Einstellungen → Rechtliches' : 'Settings → Legal'}
                        link="/admin/settings?tab=legal"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=legal'); }}
                      />
                    </div>
                  </div>

                  {/* API & Sicherheit */}
                  <div>
                    <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                      <Key className="text-gold" size={20} />
                      {guideLang === 'de' ? 'API & Sicherheit' : 'API & Security'}
                    </h3>
                    <div className="grid gap-3">
                      <FeatureBox
                        icon={Key}
                        title={guideLang === 'de' ? 'API Keys verwalten' : 'Manage API Keys'}
                        description={guideLang === 'de' 
                          ? 'Resend API Key für E-Mail-Versand. Dokumentation und Anleitung.'
                          : 'Resend API key for email sending. Documentation and guide.'}
                        location={guideLang === 'de' ? 'Einstellungen → API Keys' : 'Settings → API Keys'}
                        link="/admin/settings?tab=api-keys"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=api-keys'); }}
                      />
                      <FeatureBox
                        icon={KeyRound}
                        title={guideLang === 'de' ? 'Passwort ändern' : 'Change Password'}
                        description={guideLang === 'de' 
                          ? 'Admin-Passwort und E-Mail-Adresse ändern.'
                          : 'Change admin password and email address.'}
                        location={guideLang === 'de' ? 'Admin → Passwort ändern' : 'Admin → Change Password'}
                        link="/admin/password"
                        onNavigate={() => { setShowSetupGuide(false); navigate('/admin/password'); }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SETUP TAB */}
              {guideTab === 'setup' && (
                <div className="space-y-6">
                  {/* Step 1 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-bold shrink-0">1</div>
                    <div>
                      <h3 className="font-heading text-lg mb-2">
                        {guideLang === 'de' ? 'Grundeinstellungen' : 'Basic Settings'}
                      </h3>
                      <p className="text-sm text-[#666666] mb-2">
                        {guideLang === 'de' 
                          ? <>Gehen Sie zu <strong>Einstellungen → Allgemein</strong> und konfigurieren Sie:</>
                          : <>Go to <strong>Settings → General</strong> and configure:</>}
                      </p>
                      <ul className="text-sm text-[#666666] list-disc list-inside space-y-1">
                        <li>{guideLang === 'de' ? 'Firmenname und Logo hochladen' : 'Company name and upload logo'}</li>
                        <li>{guideLang === 'de' ? 'Kontaktdaten (Adresse, Telefon, E-Mail)' : 'Contact info (address, phone, email)'}</li>
                        <li>{guideLang === 'de' ? 'Öffnungszeiten' : 'Opening hours'}</li>
                        <li>{guideLang === 'de' ? 'Admin E-Mail für Benachrichtigungen' : 'Admin email for notifications'}</li>
                      </ul>
                      <button 
                        onClick={() => { setShowSetupGuide(false); navigate('/admin/settings'); }}
                        className="mt-3 text-sm text-gold hover:underline flex items-center gap-1"
                      >
                        {guideLang === 'de' ? 'Zu Einstellungen' : 'Go to Settings'} <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-bold shrink-0">2</div>
                    <div>
                      <h3 className="font-heading text-lg mb-2">
                        {guideLang === 'de' ? 'Module aktivieren' : 'Activate Modules'}
                      </h3>
                      <p className="text-sm text-[#666666] mb-2">
                        {guideLang === 'de' 
                          ? <>Unter <strong>Einstellungen → Module</strong> können Sie:</>
                          : <>Under <strong>Settings → Modules</strong> you can:</>}
                      </p>
                      <ul className="text-sm text-[#666666] list-disc list-inside space-y-1">
                        <li>{guideLang === 'de' ? 'Homepage-Sektionen ein-/ausschalten' : 'Enable/disable homepage sections'}</li>
                        <li>{guideLang === 'de' ? 'Reihenfolge per Pfeiltasten ändern' : 'Change order with arrow buttons'}</li>
                        <li>{guideLang === 'de' ? '⚙️ Klicken für detaillierte Einstellungen' : '⚙️ Click for detailed settings'}</li>
                        <li>{guideLang === 'de' ? 'Team & Blog-Seite aktivieren' : 'Enable Team & Blog page'}</li>
                      </ul>
                      <button 
                        onClick={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=modules'); }}
                        className="mt-3 text-sm text-gold hover:underline flex items-center gap-1"
                      >
                        {guideLang === 'de' ? 'Zu Module' : 'Go to Modules'} <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-bold shrink-0">3</div>
                    <div>
                      <h3 className="font-heading text-lg mb-2">
                        {guideLang === 'de' ? 'Fahrzeuge anlegen' : 'Add Vehicles'}
                      </h3>
                      <p className="text-sm text-[#666666] mb-2">
                        {guideLang === 'de' 
                          ? <>Klicken Sie auf <strong>Neues Fahrzeug</strong> und füllen Sie aus:</>
                          : <>Click <strong>New Vehicle</strong> and fill in:</>}
                      </p>
                      <ul className="text-sm text-[#666666] list-disc list-inside space-y-1">
                        <li>{guideLang === 'de' ? 'Marke, Modell, Preis, Baujahr' : 'Brand, model, price, year'}</li>
                        <li>{guideLang === 'de' ? 'Technische Daten (PS, Kraftstoff, Getriebe)' : 'Technical data (HP, fuel, transmission)'}</li>
                        <li>{guideLang === 'de' ? 'Fotos hochladen oder URLs einfügen' : 'Upload photos or paste URLs'}</li>
                        <li>{guideLang === 'de' ? 'Beschreibung (DE & EN)' : 'Description (DE & EN)'}</li>
                        <li>{guideLang === 'de' ? '"Hervorgehoben" für Featured-Anzeige' : '"Featured" for homepage display'}</li>
                      </ul>
                      <button 
                        onClick={() => { setShowSetupGuide(false); navigate('/admin/cars/new'); }}
                        className="mt-3 text-sm text-gold hover:underline flex items-center gap-1"
                      >
                        {guideLang === 'de' ? 'Neues Fahrzeug' : 'New Vehicle'} <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-bold shrink-0">4</div>
                    <div>
                      <h3 className="font-heading text-lg mb-2">
                        {guideLang === 'de' ? 'Verträge & Kunden' : 'Contracts & Customers'}
                      </h3>
                      <p className="text-sm text-[#666666] mb-2">
                        {guideLang === 'de' ? 'Verwalten Sie Ihre Geschäftsbeziehungen:' : 'Manage your business relationships:'}
                      </p>
                      <ul className="text-sm text-[#666666] list-disc list-inside space-y-1">
                        <li><strong>{guideLang === 'de' ? 'Kunden:' : 'Customers:'}</strong> {guideLang === 'de' ? 'Kontakte anlegen, Interesse zuweisen' : 'Create contacts, assign interest'}</li>
                        <li><strong>{guideLang === 'de' ? 'Verträge:' : 'Contracts:'}</strong> {guideLang === 'de' ? 'Kaufvertrag erstellen, per E-Mail senden' : 'Create purchase contract, send via email'}</li>
                        <li>{guideLang === 'de' ? 'Kunde kann digital unterschreiben' : 'Customer can sign digitally'}</li>
                        <li>{guideLang === 'de' ? 'Status wird automatisch aktualisiert' : 'Status updates automatically'}</li>
                      </ul>
                      <div className="flex gap-3 mt-3">
                        <button 
                          onClick={() => { setShowSetupGuide(false); navigate('/admin/customers'); }}
                          className="text-sm text-gold hover:underline flex items-center gap-1"
                        >
                          {guideLang === 'de' ? 'Kunden' : 'Customers'} <ChevronRight size={14} />
                        </button>
                        <button 
                          onClick={() => { setShowSetupGuide(false); navigate('/admin/contracts'); }}
                          className="text-sm text-gold hover:underline flex items-center gap-1"
                        >
                          {guideLang === 'de' ? 'Verträge' : 'Contracts'} <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-bold shrink-0">5</div>
                    <div>
                      <h3 className="font-heading text-lg mb-2">
                        {guideLang === 'de' ? 'Rechtliche Seiten' : 'Legal Pages'}
                      </h3>
                      <p className="text-sm text-[#666666] mb-2">
                        {guideLang === 'de' 
                          ? <>Unter <strong>Einstellungen → Rechtliches</strong>:</>
                          : <>Under <strong>Settings → Legal</strong>:</>}
                      </p>
                      <ul className="text-sm text-[#666666] list-disc list-inside space-y-1">
                        <li>{guideLang === 'de' ? 'Impressum ausfüllen (Pflicht!)' : 'Fill in imprint (required!)'}</li>
                        <li>{guideLang === 'de' ? 'Datenschutzerklärung anpassen' : 'Customize privacy policy'}</li>
                        <li>{guideLang === 'de' ? 'AGB für Kaufverträge' : 'Terms for purchase contracts'}</li>
                      </ul>
                      <button 
                        onClick={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=legal'); }}
                        className="mt-3 text-sm text-gold hover:underline flex items-center gap-1"
                      >
                        {guideLang === 'de' ? 'Zu Rechtliches' : 'Go to Legal'} <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Step 6 - API Keys */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-bold shrink-0">6</div>
                    <div>
                      <h3 className="font-heading text-lg mb-2">
                        {guideLang === 'de' ? 'API Keys einrichten' : 'Set Up API Keys'}
                      </h3>
                      <p className="text-sm text-[#666666] mb-2">
                        {guideLang === 'de' 
                          ? <>Für E-Mail-Funktionen benötigen Sie einen <strong>Resend API Key</strong>:</>
                          : <>For email functions you need a <strong>Resend API Key</strong>:</>}
                      </p>
                      <ul className="text-sm text-[#666666] list-disc list-inside space-y-1">
                        <li>{guideLang === 'de' ? 'Newsletter versenden' : 'Send newsletters'}</li>
                        <li>{guideLang === 'de' ? 'Verträge per E-Mail senden' : 'Send contracts via email'}</li>
                        <li>{guideLang === 'de' ? 'Anfragen beantworten' : 'Reply to inquiries'}</li>
                        <li>{guideLang === 'de' ? 'Preisalarm-Benachrichtigungen' : 'Price alert notifications'}</li>
                      </ul>
                      <button 
                        onClick={() => { setShowSetupGuide(false); navigate('/admin/settings?tab=api-keys'); }}
                        className="mt-3 text-sm text-gold hover:underline flex items-center gap-1"
                      >
                        {guideLang === 'de' ? 'Zu API Keys' : 'Go to API Keys'} <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Tip Box */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>💡 {guideLang === 'de' ? 'Tipp:' : 'Tip:'}</strong> {guideLang === 'de' 
                        ? 'Testen Sie die Website regelmäßig auf verschiedenen Geräten. Aktivieren Sie den Dark Mode in den Design-Einstellungen für ein modernes Erscheinungsbild.'
                        : 'Test the website regularly on different devices. Enable Dark Mode in Design settings for a modern look.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-[#E5E5E5] p-4 md:p-6">
              <button
                onClick={() => setShowSetupGuide(false)}
                className="btn-primary w-full"
              >
                {guideLang === 'de' ? 'Verstanden, loslegen!' : 'Got it, let\'s go!'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Online Status Modal */}
      {showOnlineModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="font-heading text-xl">{t('online_settings')}</h3>
              <button
                onClick={() => setShowOnlineModal(false)}
                className="p-2 hover:bg-[#F9F9F7] rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Manual Override */}
              <div className="p-4 bg-[#F9F9F7] rounded-lg">
                <h4 className="font-medium mb-3">{t('manual_status')}</h4>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setManualOnlineOverride(true)}
                    className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                      manualOnlineOverride === true 
                        ? 'bg-green-500 text-white border-green-500' 
                        : 'border-[#E5E5E5] hover:border-green-500'
                    }`}
                  >
                    🟢 {t('always_online')}
                  </button>
                  <button
                    onClick={() => setManualOnlineOverride(false)}
                    className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                      manualOnlineOverride === false 
                        ? 'bg-gray-500 text-white border-gray-500' 
                        : 'border-[#E5E5E5] hover:border-gray-500'
                    }`}
                  >
                    ⚫ {t('always_offline')}
                  </button>
                  <button
                    onClick={() => setManualOnlineOverride(null)}
                    className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                      manualOnlineOverride === null 
                        ? 'bg-gold text-white border-gold' 
                        : 'border-[#E5E5E5] hover:border-gold'
                    }`}
                  >
                    🕐 {t('by_schedule')}
                  </button>
                </div>
              </div>

              {/* Schedule */}
              {manualOnlineOverride === null && (
                <div>
                  <h4 className="font-medium mb-3">{t('schedule')}</h4>
                  <div className="space-y-2">
                    {[
                      { key: 'monday', de: 'Montag', en: 'Monday' },
                      { key: 'tuesday', de: 'Dienstag', en: 'Tuesday' },
                      { key: 'wednesday', de: 'Mittwoch', en: 'Wednesday' },
                      { key: 'thursday', de: 'Donnerstag', en: 'Thursday' },
                      { key: 'friday', de: 'Freitag', en: 'Friday' },
                      { key: 'saturday', de: 'Samstag', en: 'Saturday' },
                      { key: 'sunday', de: 'Sonntag', en: 'Sunday' }
                    ].map((day) => {
                      const dayHours = onlineHours[day.key] || { enabled: false, start: '09:00', end: '18:00' };
                      
                      return (
                        <div key={day.key} className="flex items-center gap-3 p-2 bg-[#F9F9F7] rounded-lg">
                          <label className="flex items-center gap-2 w-28">
                            <input
                              type="checkbox"
                              checked={dayHours.enabled}
                              onChange={(e) => {
                                setOnlineHours(prev => ({
                                  ...prev,
                                  [day.key]: { ...dayHours, enabled: e.target.checked }
                                }));
                              }}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm">{adminLang === 'de' ? day.de : day.en}</span>
                          </label>
                          {dayHours.enabled ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="time"
                                value={dayHours.start}
                                onChange={(e) => {
                                  setOnlineHours(prev => ({
                                    ...prev,
                                    [day.key]: { ...dayHours, start: e.target.value }
                                  }));
                                }}
                                className="px-2 py-1 border border-[#E5E5E5] rounded text-sm"
                              />
                              <span className="text-sm text-[#666666]">-</span>
                              <input
                                type="time"
                                value={dayHours.end}
                                onChange={(e) => {
                                  setOnlineHours(prev => ({
                                    ...prev,
                                    [day.key]: { ...dayHours, end: e.target.value }
                                  }));
                                }}
                                className="px-2 py-1 border border-[#E5E5E5] rounded text-sm"
                              />
                            </div>
                          ) : (
                            <span className="text-sm text-[#999999]">{t('closed')}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-[#E5E5E5] flex justify-end gap-3">
              <button
                onClick={() => setShowOnlineModal(false)}
                className="px-4 py-2 border border-[#E5E5E5] rounded-lg hover:bg-[#F9F9F7]"
              >
                {t('cancel')}
              </button>
              <button
                onClick={saveOnlineSettings}
                className="px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold/90"
              >
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Feature Box Component
function FeatureBox({ icon: Icon, title, description, location, link, onNavigate }) {
  return (
    <div className="border border-[#E5E5E5] rounded-lg p-4 hover:border-gold/50 hover:bg-gold/5 transition-all group">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
          <Icon size={16} className="text-green-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm mb-1">{title}</h4>
          <p className="text-xs text-[#666666] mb-2">{description}</p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#999999]">📍 {location}</span>
            <button 
              onClick={onNavigate}
              className="text-xs text-gold hover:underline flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              Öffnen <ChevronRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper component that provides the AdminLanguageProvider
export default function AdminLayoutWrapper() {
  return (
    <AdminLanguageProvider>
      <AdminLayout />
    </AdminLanguageProvider>
  );
}
